// VA Daily Activity Tracker — top-level view.
//
// Two faces in one component, chosen by the caller's role:
//   • VA: their own scorecard (5 headline KPIs vs target, progress
//         bars, today's activity feed)
//   • Agent/Owner: team leaderboard sorted by appointments booked +
//                  a filterable activity feed across the team
//
// Heartbeat hook: POSTs to /api/va/session every 60s while the tab
// is visible so active_hours stays accurate. Fires 'end' via
// sendBeacon on tab close.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { Activity, Users, RefreshCcw, ChevronRight, Phone, MessageSquare, Calendar, CheckCircle2, Clock, TrendingUp, Trophy } from 'lucide-react';
import { supabase } from '../lib/supabase';

async function apiFetch(path, opts = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...opts,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(opts.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

const fmt   = (n) => Number(n || 0).toLocaleString('en-US');
const fmtMin = (m) => m == null ? '—' : `${Math.round(m)}m`;
const fmtAgo = (iso) => {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const ACTION_ICON = {
  call_placed:           Phone,
  call_connected:        Phone,
  call_voicemail:        Phone,
  sms_sent:              MessageSquare,
  sms_inbound_replied:   MessageSquare,
  email_sent:            MessageSquare,
  appointment_booked:    Calendar,
  appointment_confirmed: Calendar,
  appointment_kept:      Calendar,
  followup_completed:    CheckCircle2,
  lead_contacted:        TrendingUp,
  lead_dispositioned:    CheckCircle2,
  note_added:            CheckCircle2,
};

const WINDOWS = [
  { key: 'today', label: 'Today' },
  { key: '7d',    label: 'Last 7d' },
  { key: '30d',   label: 'Last 30d' },
];

// Heartbeat — keeps va_sessions accurate. MUST be mounted at the
// CRM root (not inside this view) so total active-hours covers the
// whole CRM session, not just time spent on the VA Tracker tab. We
// export it so CommissionTracker can wire it once and never
// re-mount per tab change.
export function useSessionHeartbeat() {
  React.useEffect(() => {
    let cancelled = false;
    const ping = async (action) => {
      try {
        const token = readPersistedJwtDirect();
        if (!token) return;
        await fetch('/api/va/session', {
          method: 'POST',
          headers: { 'content-type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ action }),
        });
      } catch { /* best-effort */ }
    };
    ping('start');
    const id = setInterval(() => {
      if (cancelled) return;
      if (document.visibilityState === 'visible') ping('beat');
    }, 60_000);
    const onBeforeUnload = () => {
      try {
        // sendBeacon doesn't carry the Authorization header so this
        // CAN'T end the session reliably for a one-tab session. We
        // accept this — the next heartbeat or the IDLE_AFTER_SEC
        // timeout server-side will close it.
        navigator.sendBeacon?.('/api/va/session', new Blob([JSON.stringify({ action: 'end' })], { type: 'application/json' }));
      } catch { /* ignore */ }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, []);
}

export default function VATrackerView({ currentUser, showToast = () => {} }) {
  // Heartbeat is mounted at the CRM root via CommissionTracker so it
  // tracks the entire session (not just time on this tab). Don't
  // re-mount it here.
  const [data, setData]     = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [win, setWin]       = React.useState('today');
  const [feedFilter, setFeedFilter] = React.useState('all');

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      setData(await apiFetch(`/api/va/scorecard?window=${win}`));
    } catch (err) { showToast(`Load failed: ${err.message}`); }
    finally { setLoading(false); }
  }, [win, showToast]);
  React.useEffect(() => { load(); }, [load]);

  // Live updates when va_activity_events lands.
  React.useEffect(() => {
    if (!currentUser?.id) return;
    const ch = supabase.channel(`va_activity_${currentUser.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'va_activity_events' }, () => load())
      .subscribe();
    return () => { try { supabase.removeChannel(ch); } catch {} };
  }, [currentUser?.id, load]);

  if (loading && !data) return <div className="text-sm text-slate-500 p-4">Loading team activity…</div>;
  if (!data)   return null;

  const isVa = data.role === 'va';
  const feed = feedFilter === 'all' ? data.feed : data.feed.filter(r => r.actor_user_id === feedFilter);

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-slate-900 to-amber-900 text-white rounded-xl p-5">
        <div className="text-amber-300 text-[0.55rem] font-black uppercase tracking-[0.3em]">
          {isVa ? 'Your daily scorecard' : 'Team activity tracker'}
        </div>
        <h1 className="text-xl font-bold mt-1">
          {isVa ? 'Stack the day. Hit the targets.' : 'See who\'s producing in 10 seconds.'}
        </h1>
        <p className="text-slate-300 text-sm mt-2">
          {isVa
            ? 'Five things move the needle: leads contacted, appointments booked, follow-ups cleared, speed-to-lead, and active hours. Aim for green on all five.'
            : 'Outputs + outcomes, not just activity. Speed-to-lead and appointments booked are the numbers that predict commission.'}
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {WINDOWS.map(w => (
          <button key={w.key} onClick={() => setWin(w.key)}
            className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-md border-2 ${win === w.key ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-300 text-slate-700'}`}>
            {w.label}
          </button>
        ))}
        <button onClick={load} className="ml-auto text-slate-400 hover:text-slate-700"><RefreshCcw size={14}/></button>
      </div>

      {/* Self scorecard — always shown, even for owners (they see their own row too) */}
      {data.self && <SelfScorecard self={data.self} targets={data.targets}/>}

      {/* Manager view: leaderboard */}
      {!isVa && data.leaderboard.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
            <Trophy size={14} className="text-amber-600"/>
            <h3 className="font-bold text-slate-900">Team leaderboard — {win}</h3>
            <span className="text-xs text-slate-500 ml-auto">Sorted by appointments booked</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Member</th>
                  <th className="text-center px-3 py-2">Role</th>
                  <th className="text-right px-3 py-2">Leads</th>
                  <th className="text-right px-3 py-2">Appts</th>
                  <th className="text-right px-3 py-2">F/U</th>
                  <th className="text-right px-3 py-2">Speed</th>
                  <th className="text-right px-3 py-2">Hrs</th>
                  <th className="text-right px-3 py-2">Booked %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.leaderboard.map(r => (
                  <tr key={r.actor_user_id} className={`hover:bg-slate-50 ${r.is_self ? 'bg-amber-50/50' : ''}`}>
                    <td className="px-3 py-2">
                      <button onClick={() => setFeedFilter(r.actor_user_id === feedFilter ? 'all' : r.actor_user_id)} className="text-left">
                        <div className="font-bold text-slate-900">{r.name} {r.is_self && <span className="text-[10px] text-amber-700">(you)</span>}</div>
                        {r.upline_user_id && <div className="text-[10px] text-violet-600">⤷ downline</div>}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-center text-[10px] font-bold uppercase tracking-wider text-slate-500">{r.role}</td>
                    <td className="px-3 py-2 text-right">{fmt(r.leads_contacted)}</td>
                    <td className={`px-3 py-2 text-right font-bold ${r.appointments_booked > 0 ? 'text-emerald-700' : ''}`}>{fmt(r.appointments_booked)}</td>
                    <td className="px-3 py-2 text-right">{fmt(r.followups_cleared)}</td>
                    <td className={`px-3 py-2 text-right font-bold ${r.median_min_to_first_touch != null && r.median_min_to_first_touch <= 5 ? 'text-emerald-700' : 'text-slate-700'}`}>{fmtMin(r.median_min_to_first_touch)}</td>
                    <td className="px-3 py-2 text-right">{r.active_hours}h</td>
                    <td className="px-3 py-2 text-right text-xs text-slate-600">{r.booking_rate == null ? '—' : `${r.booking_rate}%`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Activity feed */}
      <div className="bg-white border border-slate-200 rounded-xl">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-bold text-slate-900 inline-flex items-center gap-2">
            <Activity size={14} className="text-slate-500"/>
            Activity feed
            {feedFilter !== 'all' && <span className="text-xs text-slate-500 font-normal">· filtered</span>}
          </h3>
          {feedFilter !== 'all' && <button onClick={() => setFeedFilter('all')} className="text-xs text-slate-500 hover:text-slate-900 underline">Clear filter</button>}
          <span className="text-xs text-slate-500">{feed.length} event{feed.length === 1 ? '' : 's'}</span>
        </div>
        {feed.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-10">No events in this window.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {feed.map(row => {
              const Icon = ACTION_ICON[row.action] || Activity;
              return (
                <div key={row.id} className="px-4 py-2.5 flex items-start gap-3">
                  <div className="mt-0.5 p-1.5 rounded-md bg-slate-100">
                    <Icon size={14} className="text-slate-600"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">
                      <span className="font-semibold text-slate-900">{row.actor_name}</span>
                      <span className="text-slate-500 mx-1">·</span>
                      <span className="text-slate-700">{row.action.replace(/_/g, ' ')}</span>
                      {row.outcome && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">{row.outcome}</span>}
                    </div>
                  </div>
                  <div className="text-[10px] text-slate-400 flex-shrink-0">{fmtAgo(row.created_at)}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function SelfScorecard({ self, targets }) {
  return (
    <div className="bg-white border-2 border-slate-200 rounded-2xl p-5">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <h3 className="font-bold text-slate-900 text-lg">{self.name} {self.is_self && <span className="text-xs text-slate-500">— that's you</span>}</h3>
          <div className="text-xs text-slate-500 inline-flex items-center gap-2">
            <Clock size={11}/>
            {self.first_activity_at ? `Started ${new Date(self.first_activity_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : 'Not started yet today'}
            {self.active_hours > 0 && <> · <strong>{self.active_hours}h</strong> active</>}
          </div>
        </div>
        {self.median_min_to_first_touch != null && (
          <div className="bg-slate-100 rounded-md px-3 py-1.5">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Speed-to-lead</div>
            <div className={`text-lg font-black ${self.median_min_to_first_touch <= targets.median_min_to_first_touch ? 'text-emerald-700' : 'text-amber-700'}`}>
              {fmtMin(self.median_min_to_first_touch)}
            </div>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiBar label="Leads contacted"      value={self.leads_contacted}    target={targets.leads_contacted}    progress={self.progress.leads_contacted}    accent="sky"/>
        <KpiBar label="Appointments booked"  value={self.appointments_booked} target={targets.appointments_booked} progress={self.progress.appointments_booked} accent="emerald"/>
        <KpiBar label="Follow-ups cleared"   value={self.followups_cleared}   target={targets.followups_cleared}   progress={self.progress.followups_cleared}   accent="violet"/>
        <KpiBar label="Active hours"         value={`${self.active_hours}h`}  target={`${targets.active_hours}h`}  progress={self.progress.active_hours}        accent="amber"/>
      </div>
    </div>
  );
}

function KpiBar({ label, value, target, progress, accent }) {
  const map = {
    sky:     { bg: 'bg-sky-500',     bgLight: 'bg-sky-100',     text: 'text-sky-900' },
    emerald: { bg: 'bg-emerald-500', bgLight: 'bg-emerald-100', text: 'text-emerald-900' },
    violet:  { bg: 'bg-violet-500',  bgLight: 'bg-violet-100',  text: 'text-violet-900' },
    amber:   { bg: 'bg-amber-500',   bgLight: 'bg-amber-100',   text: 'text-amber-900' },
  }[accent] || { bg: 'bg-slate-500', bgLight: 'bg-slate-100', text: 'text-slate-900' };
  return (
    <div className={`rounded-lg p-3 ${map.bgLight}`}>
      <div className={`text-[10px] font-bold uppercase tracking-wider opacity-70 ${map.text}`}>{label}</div>
      <div className={`text-2xl font-black mt-0.5 ${map.text}`}>{value}</div>
      <div className="text-[10px] opacity-60 mt-0.5">of {target}</div>
      <div className="mt-2 h-1.5 rounded-full bg-white/60 overflow-hidden">
        <div className={`h-full ${map.bg}`} style={{ width: `${Math.min(100, progress || 0)}%` }}/>
      </div>
    </div>
  );
}
